<script setup lang="ts">
	import { onBeforeUnmount, onMounted, ref } from "vue";

	const props = withDefaults(
		defineProps<{
			modelValue: string | string[];
			count: number;
			error: string;
			darkMode?: boolean;
			capital?: boolean;
		}>(),
		{
			darkMode: false,
			capital: false,
		},
	);

	const list: any = [];
	for (let i = 0; i < Number(props.count); i++) {
		list.push("");
	}
	const otpDigits = ref([...list]);
	const otpContainer = ref();
	const inputs = ref();

	const emit = defineEmits(["update:modelValue"]);

	const onInput = (index: number) => {
		if (otpDigits.value[index].length === 1) {
			if (index < otpDigits.value.length - 1) {
				inputs.value[index + 1].focus();
			}
			validateAndEmit();
		}
	};

	const onKeyDown = (event: any, index: number) => {
		if (
			(event.key === "Backspace" || event.key === "Delete") &&
			otpDigits.value[index] === "" &&
			index > 0
		) {
			inputs.value[index - 1].focus();
		}
		validateAndEmit();
	};

	const focus = (event: any) => {
		if (event) {
			event.target.select();
		}
	};

	const validateAndEmit = () => {
		emit("update:modelValue", otpDigits);
	};

	const handlePaste = (event: any) => {
		event.preventDefault();
		const pastedText = event.clipboardData.getData("text").slice(0, 4);
		for (let i = 0; i < 4; i++) {
			otpDigits.value[i] = pastedText[i] || "";
		}

		// Focus on the next empty input or the last input if all are filled
		const nextEmptyIndex = otpDigits.value.findIndex(
			(digit) => digit === "",
		);
		const focusIndex =
			nextEmptyIndex === -1 ? otpDigits.value.length - 1 : nextEmptyIndex;
		inputs.value[focusIndex].focus();

		validateAndEmit();
	};

	onMounted(() => {
		if (otpContainer.value) {
			otpContainer.value.addEventListener("paste", handlePaste);
		}
	});

	onBeforeUnmount(() => {
		otpContainer.value.removeEventListener("paste", handlePaste);
	});
</script>

<template>
	<div class="flex flex-col items-center">
		<div ref="otpContainer" dir="ltr" class="flex gap-2">
			<input
				v-for="(_, index) in otpDigits"
				:id="index === 0 ? 'otp-input' : `otp-input-${index}`"
				ref="inputs"
				:key="index"
				v-model="otpDigits[index]"
				autocomplete="one-time-code"
				maxlength="1"
				type="text"
				inputmode="numeric"
				pattern="[0-9]*"
				class="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none max-w-16 h-14 w-14 min-h-0 min-w-0 text-2xl text-center border rounded-2xl focus:ring-2 focus:ring-primary focus:!border-transparent"
				:class="[
					{
						'p-invalid !border-errorRed': props.error,
						'border-dark': !props.error,
					},
					{ 'border-white text-white bg-darkAccentB': darkMode },
					{ 'border-dark text-dark bg-white': !darkMode },
				]"
				@input="onInput(index)"
				@keydown="onKeyDown($event, index)"
				@focus="focus($event)"
			/>
		</div>
	</div>
</template>

<style scoped></style>
